import { ImagesSubComponent } from '../images.sub.component';
import PhotoSwipeLightbox from './photoswipe/photoswipe-lightbox';
import PhotoSwipe from './photoswipe/photoswipe';
import { getCurrentMajorBreakpoint, MajorBreakpoint } from '../../../_common/client/breakpoint';
import { FALLBACK_IMAGE_ID, getImageUrl } from '../../../_common/client/images';

type ImageSize = {
    width: number;
    height: number;
};

export interface PhotoSwipeInterface extends PhotoSwipe {
    currIndex: number;
    element: HTMLElement;
    gestures: {
        supportsTouch: boolean;
    };
}

export interface ItemData {
    alt: string;
    src: string;
    width: number;
    height: number;
    msrc?: string;
    thumbCropped: boolean;
}

export interface PhotoSwipeLightboxInterface extends PhotoSwipeLightbox {
    pswp: PhotoSwipeInterface;
    addFilter(name: 'domItemData', fn: (itemData: ItemData, slideElement: HTMLElement) => ItemData): void;
    addFilter(name: 'contentErrorElement', fn: (errorMsgEl: HTMLElement) => HTMLElement): void;
}

export class LightboxSubComponent extends ImagesSubComponent {
    private static readonly UI_IDLE_TIME = 3000;
    private static readonly MAX_IMAGE_EXTENT_MOBILE = 1200;
    private static readonly MAX_IMAGE_EXTENT_DESKTOP = 2500;

    public rootSelector = '.js_pdp_images';

    private zoomImageTrackingDone = false;

    protected onImageChanged(): void {
        return;
    }

    public async init(): Promise<void> {
        return;
    }

    public async onHtmlLoaded(): Promise<void> {
        if (!this.isPresent()) {
            return;
        }
        this.initPhotoSwipeLightBox();
    }

    private initPhotoSwipeLightBox() {
        const options = {
            gallery: '.js_pdp_main-image',
            children: '.js_pdp_main-image__slide--zoomable',
            thumbSelector: 'img',
            mainClass: 'pdp_lightbox',
            showHideAnimationType: 'zoom',
            bgOpacity: 1,
            showHideOpacity: false,
            clickToCloseNonZoomable: false,
            loop: true,
            closeSVG: LightboxSubComponent.createIconElement('pl_icon_close', 'pl_icon100').outerHTML,
            arrowPrevSVG: LightboxSubComponent.createIconElement('pl_icon_arrow-left', 'pl_icon100').outerHTML,
            arrowNextSVG: LightboxSubComponent.createIconElement('pl_icon_arrow-right', 'pl_icon100').outerHTML,
            zoomSVG:
                LightboxSubComponent.createIconElement('pl_icon_zoom-in', 'pl_icon100', 'pdp_lightbox__zoom-in-icon').outerHTML +
                LightboxSubComponent.createIconElement('pl_icon_zoom-out', 'pl_icon100', 'pdp_lightbox__zoom-out-icon').outerHTML,
            pswpModule: () => PhotoSwipe,
        };

        const lightbox = new PhotoSwipeLightbox(options) as PhotoSwipeLightboxInterface;

        let previousIndex = 0;

        lightbox.addFilter('domItemData', (itemData, slideElement) => {
            return LightboxSubComponent.extractItemDataFromSlideElement(itemData, slideElement);
        });

        lightbox.addFilter('contentErrorElement', errorMsgEl => {
            errorMsgEl.innerHTML = LightboxSubComponent.createIconElement(
                'pl_icon_error-article-image',
                'pdp_lightbox__error-icon'
            ).outerHTML;
            return errorMsgEl;
        });

        lightbox.on('firstUpdate', () => {
            previousIndex = lightbox.pswp.currIndex;
        });

        lightbox.on('openingAnimationEnd', () => {
            if (lightbox.pswp.gestures.supportsTouch) {
                LightboxSubComponent.addPinchIcon(lightbox.pswp.element);
            } else {
                LightboxSubComponent.activateHideUiOnInactivity(lightbox.pswp.element);
            }
            this.trackEvent({
                product_Zoom: 'open',
            });
        });

        lightbox.on('close', () => {
            this.trackEvent({
                product_Zoom: 'close',
            });
        });

        lightbox.on('change', () => {
            const currIndex = lightbox.pswp.currIndex;
            const numItems = lightbox.pswp.getNumItems();
            let diff = currIndex - previousIndex;
            if (diff !== 0) {
                if (Math.abs(diff) === numItems - 1) {
                    diff *= -1;
                }
                const direction = diff > 0 ? 'right' : 'left';
                previousIndex = currIndex;
                this.trackEvent({
                    product_Zoom: 'change_image',
                    product_ZoomDirection: direction,
                });
                this.changeImage(lightbox.pswp.currIndex);
            }
        });

        lightbox.on('beforeZoomTo', () => {
            if (!this.zoomImageTrackingDone) {
                this.zoomImageTrackingDone = true;
                this.trackEvent({
                    product_Zoom: 'zoom_image',
                });
            }
        });

        lightbox.init();
    }

    private static createIconElement(iconName: string, ...cssClass: string[]): SVGSVGElement {
        const svgElement = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svgElement.classList.add(...cssClass);
        svgElement.setAttribute('role', 'img');
        svgElement.innerHTML = `<use xlink:href="/assets-static/icons/${iconName}.svg#${iconName}"></use>`;
        return svgElement;
    }

    private static extractItemDataFromSlideElement(itemData: ItemData, slideElement: HTMLElement): ItemData {
        const { imageId, imageAlt, imageWidth, imageHeight } = slideElement.dataset;
        let width = Number(imageWidth);
        width = width > 0 ? width : 1500;
        let height = Number(imageHeight);
        height = height > 0 ? height : 1500;

        const maxImageExtent = LightboxSubComponent.getMaxImageExtent();
        const imageSize = LightboxSubComponent.calculateImageSize(height, width, maxImageExtent);

        const imgElement = slideElement.querySelector('img');
        itemData.src = getImageUrl(imageId || FALLBACK_IMAGE_ID, `w=${imageSize.width}&h=${imageSize.height}`);
        itemData.width = imageSize.width;
        itemData.height = imageSize.height;
        itemData.msrc = imgElement?.currentSrc;
        itemData.alt = imageAlt || '';
        itemData.thumbCropped = false;
        return itemData;
    }

    private static getMaxImageExtent(): number {
        const currentMajorBreakpoint = getCurrentMajorBreakpoint();
        return currentMajorBreakpoint === MajorBreakpoint.Mobile ? this.MAX_IMAGE_EXTENT_MOBILE : this.MAX_IMAGE_EXTENT_DESKTOP;
    }

    private static calculateImageSize(height: number, width: number, maxExtent: number): ImageSize {
        if (width >= maxExtent || height > maxExtent) {
            const largerSide: number = Math.max(width, height);
            return {
                width: Math.round((width / largerSide) * maxExtent),
                height: Math.round((height / largerSide) * maxExtent),
            };
        }
        return {
            width,
            height,
        };
    }

    private static addPinchIcon(lightboxElement: HTMLElement) {
        lightboxElement.appendChild(LightboxSubComponent.createIconElement('pl_icon_pinch-gesture', 'pl_icon', 'pdp_lightbox__pinch-icon'));
    }

    private static activateHideUiOnInactivity(lightboxElement: HTMLElement) {
        let timeout: NodeJS.Timeout;
        let uiVisible = true;

        const handler = () => {
            if (!uiVisible) {
                lightboxElement.classList.add('pswp--ui-visible');
                uiVisible = true;
            }

            clearTimeout(timeout);
            timeout = setTimeout(function () {
                lightboxElement.classList.remove('pswp--ui-visible');
                uiVisible = false;
            }, LightboxSubComponent.UI_IDLE_TIME);
        };

        lightboxElement.addEventListener('click', handler);
        lightboxElement.addEventListener('mousemove', handler);
    }
}
